import type { ConditionOperator } from '@ricoh/ria.client.core/constants/core/data.type';
import type { PatternCompositeFilterDescriptor, PatternFilterDescriptor, PatternFormField } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { isPatternCompositeFilterDescriptor } from '@ricoh/ria.client.core/utils/component/form';
import { getStringValue, isNotNull } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternAccessScope } from '~/common/logic/feature/settings/batch-type-library/pattern';
import type { FieldConditionRule } from '~/common/model/api/batch-type';
import type { Pattern } from '~/common/model/api/batch-type/pattern';
import type { DataType } from '~/common/model/api/common';

import { DATA_TYPE_GENERAL_NAMES } from '~/common/constants/feature/settings/batch-type-library';
import { VALIDATION_CONDITION_OPERATORS } from '~/common/constants/feature/settings/condition';
import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';

const logger = createLogger('logic/feature/settings/batch-type-library/field');
const IS_TRANSFORMATION_TEXT = false;

/**
 * Create Filter Field Pattern FormField from Pattern.
 *
 * @param {Pattern[]} filterFieldPattern Filter Field Pattern.
 *
 * @returns {PatternFormField} Filter Field Pattern FormField.
 */
export function createFilterFieldPatternFormField(filterFieldPattern: Pattern[]): PatternFormField {
  return filterFieldPattern.reduce<PatternFormField>((prev, pattern) => {
    let item: Nullable<PatternFormField[number]>;
    switch (pattern.$is) {
      case 'const': {
        item = patternLogic.createPatternFormFieldItem.fromPatternConstant(pattern);
        break;
      }

      case 'now':
      case 'guid':
      case 'random': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGenerate(pattern, IS_TRANSFORMATION_TEXT);
        break;
      }

      case 'global': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGlobal(pattern);
        break;
      }

      case 'batch': {
        item = patternLogic.createPatternFormFieldItem.fromPatternBatch(pattern);
        break;
      }

      case 'document': {
        item = patternLogic.createPatternFormFieldItem.fromPatternDocument(pattern);
        break;
      }

      default: {
        item = undefined;
        break;
      }
    }

    if (isNotNull(item)) {
      prev.push(item);
    } else {
      logger.writeWithData('warn', 'Invalid pattern', { cause: pattern });
    }

    return prev;
  }, []);
}

/**
 * Create Filter Field Pattern from Pattern FormField.
 *
 * @param {PatternFormField} formField Pattern FormField.
 * @param {PatternAccessScope} scope Pattern Access Scope.
 *
 * @returns {Pattern[]} Filter Field Pattern.
 */
export function createFilterFieldPattern(formField: PatternFormField, scope: PatternAccessScope): Pattern[] {
  return formField.reduce<Pattern[]>((prev, item) => {
    let pattern: Nullable<Pattern>;
    if (typeof item === 'string') {
      pattern = patternLogic.createPattern.fromText(item);
    } else {
      switch (item.tabKey) {
        case 'systemValue': {
          pattern = patternLogic.createPattern.fromSystemValue(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'globalVariable': {
          pattern = patternLogic.createPattern.fromGlobalVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchField': {
          pattern = patternLogic.createPattern.fromBatchField(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchVariable': {
          pattern = patternLogic.createPattern.fromBatchVariable(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchBusinessRule': {
          pattern = patternLogic.createPattern.fromBusinessRule(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentField': {
          pattern = patternLogic.createPattern.fromDocumentField(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentVariable': {
          pattern = patternLogic.createPattern.fromDocumentVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentBusinessRule': {
          pattern = patternLogic.createPattern.fromDocumentBusinessRule(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        default: {
          pattern = undefined;
          break;
        }
      }
    }

    if (isNotNull(pattern)) {
      prev.push(pattern);
    } else {
      logger.writeWithData('warn', 'Invalid pattern item', { cause: item });
    }

    return prev;
  }, []);
}

/**
 * Create Condition Rules FormField.
 *
 * @param {FieldConditionRule} conditionRules Condition Rules.
 *
 * @returns {PatternCompositeFilterDescriptor} Condition Rules FormField.
 */
export function createConditionRulesFormField(conditionRules: FieldConditionRule): PatternCompositeFilterDescriptor {
  const filter = (function build(source: FieldConditionRule): PatternFilterDescriptor | PatternCompositeFilterDescriptor {
    if ('group' in source) {
      return {
        logic: source.group,
        filters: source.rules?.map(build) ?? [],
      };
    }

    const { operator } = source;
    return {
      field: createFilterFieldPatternFormField(source.field),
      operator,
      value: createFilterFieldPatternFormField(source.value),
    };
  })(conditionRules);

  if (isPatternCompositeFilterDescriptor(filter)) {
    return filter;
  }

  return {
    logic: 'and',
    filters: [filter],
  };
}

/**
 * Create Condition Rule.
 *
 * @param {PatternCompositeFilterDescriptor} conditionRulesFormField Condition Rules FormField.
 * @param {Function} getFilterDataType FilterField DataType getter.
 * @param {PatternAccessScope} scope Access scope for creating pattern.
 *
 * @returns {FieldConditionRule} Condition Rules.
 */
export function createConditionRule(
  conditionRulesFormField: PatternCompositeFilterDescriptor,
  getFilterDataType: (field: Nullable<PatternFormField>) => Nullable<DataType>,
  scope: PatternAccessScope,
): FieldConditionRule {
  return (
    (function build(descriptor: PatternFilterDescriptor | PatternCompositeFilterDescriptor): Nullable<FieldConditionRule> {
      if (isPatternCompositeFilterDescriptor(descriptor)) {
        return {
          group: descriptor.logic,
          rules: descriptor.filters.map((filter) => build(filter)).filter<FieldConditionRule>((filter) => !!filter),
        };
      }

      const type = getFilterDataType(descriptor.field);
      if (!type || type.name === 'table') {
        logger.writeWithData('warn', 'Invalid type', { descriptor, type });
        return undefined;
      }

      const typeName = DATA_TYPE_GENERAL_NAMES.includes(type.name) ? type.name : 'list';
      const operator = getStringValue<ConditionOperator>(descriptor.operator);
      if (!VALIDATION_CONDITION_OPERATORS[typeName].includes(operator)) {
        logger.writeWithData('warn', 'Invalid operator', descriptor);
        return undefined;
      }

      return {
        field: createFilterFieldPattern(descriptor.field ?? [], scope),
        operator: getStringValue(descriptor.operator),
        value: createFilterFieldPattern(descriptor.value ?? [], scope),
      };
    })(conditionRulesFormField) ?? {
      group: 'and',
      rules: [],
    }
  );
}
